import React, { useContext, useEffect, useState } from 'react';
import { showError, showSuccess, showDeleteConfirm } from '../../utils/sweetAlert';
import axios from 'axios';
import { AuthContext } from '../../providers/AuthContext';
import { getAdminAuthHeaders } from '../../utils/adminApi';
import { getDisplayImageUrl } from '../../utils/imageUtils';
import { API_ENDPOINTS } from '../../config/api';

const EnhancedMediaLibrary = ({ onSelect, mode = 'dashboard', allowUpload = true }) => {
  const { token } = useContext(AuthContext);
  const [media, setMedia] = useState([]);
  const [folders, setFolders] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const [selectedFolder, setSelectedFolder] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({});

  const [uploadFolder, setUploadFolder] = useState('products');
  const [uploadTags, setUploadTags] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);

  const [viewMode, setViewMode] = useState('grid');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [selectedMediaIds, setSelectedMediaIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const MAX_FILES = 20;
  const canBulkDelete = mode === 'dashboard' && !onSelect;

  useEffect(() => {
    fetchMedia();
    fetchFolders();
    fetchTags();
  }, [selectedFolder, selectedTags, searchTerm, currentPage]);

  const fetchMedia = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: currentPage, limit: 20 });
      if (selectedFolder) params.append('folder', selectedFolder);
      if (selectedTags.length > 0) params.append('tags', selectedTags.join(','));
      if (searchTerm) params.append('search', searchTerm);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/media?${params}`);
      setMedia(response.data.media || response.data);
      setPagination(response.data.pagination || {});
    } catch (error) {
      console.error('Error al obtener medios:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFolders = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/media/folders`);
      setFolders(response.data);
    } catch (error) { console.error('Error al obtener carpetas:', error); }
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/media/tags`);
      setTags(response.data);
    } catch (error) { console.error('Error al obtener tags:', error); }
  };

  const addFilesToQueue = (newFiles) => {
    const available = MAX_FILES - selectedFiles.length;
    if (available <= 0) {
      showError(`Máximo ${MAX_FILES} archivos permitidos`);
      return;
    }
    const toAdd = newFiles.slice(0, available);
    if (newFiles.length > available) {
      showError(`Solo se agregaron ${toAdd.length} archivo(s). Límite máximo: ${MAX_FILES}`);
    }
    setSelectedFiles(prev => [...prev, ...toAdd]);
    const urls = toAdd.map(f => URL.createObjectURL(f));
    setPreviewUrls(prev => [...prev, ...urls]);
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) addFilesToQueue(files);
    event.target.value = '';
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length > 0) addFilesToQueue(files);
  };

  const removeFileFromQueue = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => {
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
  };

  const clearFileQueue = () => {
    previewUrls.forEach(url => URL.revokeObjectURL(url));
    setSelectedFiles([]);
    setPreviewUrls([]);
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    const fileCount = selectedFiles.length;
    try {
      setUploading(true);

      // 1. Request presigned PUT URLs from backend (tiny JSON payload — no Vercel limit)
      const presignPayload = selectedFiles.map((file, i) => ({
        originalname: file.name,
        contentType: file.type || 'image/jpeg',
        folder: uploadFolder,
        index: i,
      }));

      const presignRes = await axios.post(
        `${process.env.REACT_APP_API_URL}/media/presign`,
        { files: presignPayload },
        { headers: getAdminAuthHeaders(token) }
      );
      const presignedFiles = presignRes.data;

      // 2. Upload each file directly to Wasabi (bypasses Vercel entirely)
      const uploadedFiles = [];
      for (const entry of presignedFiles) {
        const { presignedUrl, key, url, originalIndex } = entry;
        const file = selectedFiles[originalIndex];

        const putRes = await fetch(presignedUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'image/jpeg' },
        });

        if (!putRes.ok) {
          throw new Error(`Error al subir ${file.name} (${putRes.status})`);
        }

        uploadedFiles.push({
          key,
          url,
          originalname: file.name,
          folder: uploadFolder,
          tags: uploadTags,
          description: uploadDescription,
          size: file.size,
          mimetype: file.type || 'image/jpeg',
        });
      }

      // 3. Register files in MongoDB
      const registerRes = await axios.post(
        `${process.env.REACT_APP_API_URL}/media/register`,
        { files: uploadedFiles },
        { headers: getAdminAuthHeaders(token) }
      );
      const registeredFiles = registerRes.data;

      clearFileQueue();
      setUploadTags('');
      setUploadDescription('');
      setShowUploadForm(false);
      fetchMedia();
      fetchFolders();
      fetchTags();
      showSuccess(fileCount > 1 ? `${fileCount} archivos subidos exitosamente` : 'Archivo subido exitosamente');

      if (mode === 'selection' && onSelect) {
        const items = Array.isArray(registeredFiles) ? registeredFiles : [registeredFiles];
        items.forEach(item => onSelect(item));
      }
    } catch (error) {
      console.error('Error al subir archivo:', error);
      const msg = error.response?.data?.error || error.message || 'Error desconocido';
      showError(`Error al subir: ${msg}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (mediaId) => {
    const result = await showDeleteConfirm('¿Estás seguro de eliminar este archivo?');
    if (!result.isConfirmed) return;
    try {
      await axios.delete(API_ENDPOINTS.media.delete(mediaId), { headers: getAdminAuthHeaders(token) });
      setSelectedMediaIds(prev => prev.filter(id => id !== mediaId));
      if (selectedMedia?._id === mediaId) setSelectedMedia(null);
      fetchMedia();
      fetchFolders();
      fetchTags();
      showSuccess('Archivo eliminado exitosamente');
    } catch (error) { showError('Error al eliminar archivo'); }
  };

  const toggleMediaSelection = (mediaId) => {
    setSelectedMediaIds(prev => (
      prev.includes(mediaId) ? prev.filter(id => id !== mediaId) : [...prev, mediaId]
    ));
  };

  const togglePageSelection = () => {
    const pageIds = media.map(item => item._id);
    const isPageSelected = pageIds.length > 0 && pageIds.every(id => selectedMediaIds.includes(id));
    setSelectedMediaIds(prev => isPageSelected
      ? prev.filter(id => !pageIds.includes(id))
      : [...new Set([...prev, ...pageIds])]
    );
  };

  const handleBulkDelete = async () => {
    if (selectedMediaIds.length === 0) return;
    const result = await showDeleteConfirm(`¿Eliminar ${selectedMediaIds.length} archivo(s) seleccionados? Esta acción no se puede deshacer.`);
    if (!result.isConfirmed) return;

    try {
      setBulkDeleting(true);
      const response = await axios.delete(API_ENDPOINTS.media.bulkDelete(), {
        headers: getAdminAuthHeaders(token),
        data: { mediaIds: selectedMediaIds }
      });
      const { deletedCount = 0, deletedIds = [], failedIds = [] } = response.data || {};

      setSelectedMediaIds(prev => prev.filter(id => !deletedIds.includes(id)));
      if (selectedMedia && deletedIds.includes(selectedMedia._id)) setSelectedMedia(null);
      await Promise.all([fetchMedia(), fetchFolders(), fetchTags()]);

      if (failedIds.length > 0) {
        showError(`Se eliminaron ${deletedCount} archivo(s), pero ${failedIds.length} no pudieron eliminarse.`);
      } else {
        showSuccess(`${deletedCount} archivo(s) eliminado(s) exitosamente`);
      }
    } catch (error) {
      showError(error.response?.data?.error || 'Error al eliminar los archivos seleccionados');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleTagToggle = (tag) => {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
    setCurrentPage(1);
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    showSuccess('URL copiada al portapapeles');
  };

  const getImageUrl = (mediaItem) => {
    return getDisplayImageUrl(mediaItem.filename);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const FOLDER_OPTIONS = [
    { value: 'products', label: 'Productos', icon: 'fa-box' },
    { value: 'categories', label: 'Categorías', icon: 'fa-folder' },
    { value: 'banners', label: 'Banners', icon: 'fa-image' },
    { value: 'general', label: 'General', icon: 'fa-photo-film' },
  ];

  return (
    <div className="medialib-panel">
      {/* Header */}
      {mode === 'dashboard' && allowUpload && (
        <div className="medialib-header">
          <div>
            <h1>Biblioteca de Medios</h1>
            <p>Gestiona las imágenes y archivos de tu tienda</p>
          </div>
          <div className="medialib-header-actions">
            <div className="medialib-view-toggle">
              <button className={`medialib-view-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')}>
                <i className="fas fa-th"></i>
              </button>
              <button className={`medialib-view-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}>
                <i className="fas fa-list"></i>
              </button>
            </div>
            <button className="medialib-upload-toggle" onClick={() => setShowUploadForm(!showUploadForm)}>
              <i className="fas fa-cloud-arrow-up"></i> Subir Archivo
            </button>
            {canBulkDelete && media.length > 0 && (
              <button className="medialib-select-page-btn" onClick={togglePageSelection}>
                <i className={`fas ${media.every(item => selectedMediaIds.includes(item._id)) ? 'fa-square-check' : 'fa-square'}`}></i>
                {media.every(item => selectedMediaIds.includes(item._id)) ? 'Quitar selección' : 'Seleccionar página'}
              </button>
            )}
          </div>
        </div>
      )}

      {canBulkDelete && selectedMediaIds.length > 0 && (
        <div className="medialib-bulk-bar">
          <span><strong>{selectedMediaIds.length}</strong> archivo(s) seleccionado(s)</span>
          <div>
            <button className="medialib-bulk-clear-btn" onClick={() => setSelectedMediaIds([])} disabled={bulkDeleting}>Limpiar selección</button>
            <button className="medialib-bulk-delete-btn" onClick={handleBulkDelete} disabled={bulkDeleting}>
              {bulkDeleting ? <><i className="fas fa-spinner fa-spin"></i> Eliminando...</> : <><i className="fas fa-trash"></i> Eliminar seleccionados</>}
            </button>
          </div>
        </div>
      )}

      {/* Upload Form */}
      {allowUpload && (showUploadForm || mode === 'selection') && (
        <div className="medialib-upload-card">
          <div className="medialib-upload-layout-vertical">
            <div className="medialib-upload-fields">
              <div className="medialib-field">
                <label>Archivos</label>
                <div
                  className={`medialib-file-input${isDragging ? ' drag-over' : ''}`}
                  onClick={() => document.getElementById(`medialib-file-pick-${mode}`).click()}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <i className={`fas ${isDragging ? 'fa-cloud-arrow-down' : 'fa-file-image'}`}></i>
                  <span>
                    {isDragging
                      ? 'Suelta las imágenes aquí...'
                      : selectedFiles.length > 0
                        ? `${selectedFiles.length}/${MAX_FILES} archivo(s) seleccionado(s)`
                        : 'Arrastra imágenes aquí o haz clic para seleccionar (máx. 20)'}
                  </span>
                  <input id={`medialib-file-pick-${mode}`} type="file" accept="image/*" multiple onChange={handleFileSelect} style={{ display: 'none' }} />
                </div>
              </div>
              <div className="medialib-upload-fields-row">
                <div className="medialib-field" style={{ flex: 1 }}>
                  <label>Carpeta</label>
                  <div className="medialib-folder-chips">
                    {FOLDER_OPTIONS.map(f => (
                      <button key={f.value} className={`medialib-folder-chip ${uploadFolder === f.value ? 'active' : ''}`}
                        onClick={() => setUploadFolder(f.value)}>
                        <i className={`fas ${f.icon}`}></i> {f.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="medialib-field" style={{ flex: 1 }}>
                  <label>Tags (separados por comas)</label>
                  <input type="text" value={uploadTags} onChange={(e) => setUploadTags(e.target.value)} placeholder="producto, ropa, promoción" />
                </div>
              </div>
              <div className="medialib-field">
                <label>Descripción</label>
                <textarea rows="2" value={uploadDescription} onChange={(e) => setUploadDescription(e.target.value)}
                  placeholder="Descripción del archivo..." />
              </div>
              <div className="medialib-upload-actions">
                <button className="medialib-submit-btn" onClick={handleUpload} disabled={selectedFiles.length === 0 || uploading}>
                  {uploading ? <><i className="fas fa-spinner fa-spin"></i> Subiendo...</> : <><i className="fas fa-upload"></i> Subir {selectedFiles.length > 1 ? `(${selectedFiles.length})` : ''}</>}
                </button>
                {mode !== 'selection' && (
                  <button className="medialib-cancel-btn" onClick={() => { setShowUploadForm(false); clearFileQueue(); }}>
                    Cancelar
                  </button>
                )}
              </div>
            </div>
            {previewUrls.length > 0 && (
              <div className="medialib-preview-queue">
                <div className="medialib-preview-queue-header">
                  <span>{selectedFiles.length} archivo(s) en cola</span>
                  <button type="button" className="medialib-clear-queue" onClick={clearFileQueue}>
                    <i className="fas fa-trash"></i> Limpiar todo
                  </button>
                </div>
                <div className="medialib-preview-queue-grid">
                  {previewUrls.map((url, idx) => (
                    <div key={idx} className="medialib-preview-queue-item">
                      <img src={url} alt={`Preview ${idx + 1}`} />
                      <button type="button" className="medialib-preview-queue-remove" onClick={() => removeFileFromQueue(idx)} title="Quitar">
                        <i className="fas fa-times"></i>
                      </button>
                      <span className="medialib-preview-queue-name">{selectedFiles[idx]?.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="medialib-toolbar">
        <div className="medialib-search">
          <i className="fas fa-search"></i>
          <input type="text" placeholder="Buscar archivos..." value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
        </div>
        <select className="medialib-select" value={selectedFolder}
          onChange={(e) => { setSelectedFolder(e.target.value); setCurrentPage(1); }}>
          <option value="">Todas las carpetas</option>
          {folders.map(folder => <option key={folder} value={folder}>{folder}</option>)}
        </select>
      </div>

      {/* Tags */}
      {tags.length > 0 && (
        <div className="medialib-tags-row">
          {tags.slice(0, 12).map(tag => (
            <button key={tag} className={`medialib-tag-chip ${selectedTags.includes(tag) ? 'active' : ''}`}
              onClick={() => handleTagToggle(tag)}>{tag}</button>
          ))}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="medialib-loading">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Cargando archivos...</p>
        </div>
      ) : media.length === 0 ? (
        <div className="medialib-empty">
          <i className="fas fa-photo-film"></i>
          <h3>No hay archivos</h3>
          <p>Sube tu primer archivo o ajusta los filtros</p>
        </div>
      ) : (
        <>
          {viewMode === 'grid' ? (
            <div className="medialib-grid">
              {media.map(item => (
                <div key={item._id} className={`medialib-card ${selectedMediaIds.includes(item._id) ? 'selected' : ''}`}>
                  {canBulkDelete && (
                    <label className="medialib-card-select" onClick={(event) => event.stopPropagation()}>
                      <input type="checkbox" checked={selectedMediaIds.includes(item._id)} onChange={() => toggleMediaSelection(item._id)} />
                      <span><i className="fas fa-check"></i></span>
                    </label>
                  )}
                  <div className="medialib-card-img" onClick={() => onSelect ? onSelect(item) : setSelectedMedia(item)}>
                    <img src={getImageUrl(item)} alt={item.originalname} />
                    <div className="medialib-card-overlay">
                      <i className="fas fa-expand"></i>
                    </div>
                  </div>
                  <div className="medialib-card-body">
                    <span className="medialib-card-name" title={item.originalname}>{item.originalname}</span>
                    <div className="medialib-card-meta">
                      <span className="medialib-card-folder"><i className="fas fa-folder"></i> {item.folder}</span>
                      <span>{formatFileSize(item.size)}</span>
                    </div>
                    {item.tags?.length > 0 && (
                      <div className="medialib-card-tags">
                        {item.tags.slice(0, 2).map(tag => (
                          <span key={tag} className="medialib-card-tag">{tag}</span>
                        ))}
                      </div>
                    )}
                    <div className="medialib-card-actions">
                      <button className="medialib-action-btn" onClick={() => copyToClipboard(getImageUrl(item))} title="Copiar URL">
                        <i className="fas fa-copy"></i>
                      </button>
                      {onSelect && (
                        <button className="medialib-action-btn medialib-action-select" onClick={() => onSelect(item)}>Seleccionar</button>
                      )}
                      <button className="medialib-action-btn medialib-action-delete" onClick={() => handleDelete(item._id)} title="Eliminar">
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="medialib-table-card">
              <table className="medialib-table">
                <thead>
                  <tr>
                    {canBulkDelete && (
                      <th style={{ width: '44px' }}>
                        <input
                          type="checkbox"
                          checked={media.length > 0 && media.every(item => selectedMediaIds.includes(item._id))}
                          onChange={togglePageSelection}
                          aria-label="Seleccionar archivos de esta página"
                        />
                      </th>
                    )}
                    <th style={{ width: '60px' }}>Preview</th>
                    <th>Nombre</th>
                    <th>Carpeta</th>
                    <th>Tamaño</th>
                    <th>Tags</th>
                    <th>Fecha</th>
                    <th style={{ textAlign: 'center' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {media.map(item => (
                    <tr key={item._id} className={selectedMediaIds.includes(item._id) ? 'selected' : ''}>
                      {canBulkDelete && (
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedMediaIds.includes(item._id)}
                            onChange={() => toggleMediaSelection(item._id)}
                            aria-label={`Seleccionar ${item.originalname}`}
                          />
                        </td>
                      )}
                      <td><img src={getImageUrl(item)} alt={item.originalname} className="medialib-table-thumb" /></td>
                      <td><span className="medialib-table-name">{item.originalname}</span></td>
                      <td><span className="medialib-table-folder">{item.folder}</span></td>
                      <td>{formatFileSize(item.size)}</td>
                      <td>
                        {item.tags?.map(tag => (
                          <span key={tag} className="medialib-card-tag">{tag}</span>
                        ))}
                      </td>
                      <td>{new Date(item.uploadDate).toLocaleDateString()}</td>
                      <td style={{ textAlign: 'center' }}>
                        <div className="medialib-table-actions">
                          <button className="medialib-action-btn" onClick={() => copyToClipboard(getImageUrl(item))} title="Copiar URL">
                            <i className="fas fa-copy"></i>
                          </button>
                          {onSelect && (
                            <button className="medialib-action-btn medialib-action-select" onClick={() => onSelect(item)}>Sel.</button>
                          )}
                          <button className="medialib-action-btn medialib-action-delete" onClick={() => handleDelete(item._id)}>
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="medialib-pagination">
              <button className="medialib-page-btn" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
                <i className="fas fa-chevron-left"></i> Anterior
              </button>
              <span className="medialib-page-info">Página {currentPage} de {pagination.pages}</span>
              <button className="medialib-page-btn" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === pagination.pages}>
                Siguiente <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}
        </>
      )}

      {/* Preview Modal */}
      {selectedMedia && !onSelect && (
        <div className="medialib-modal-overlay" onClick={() => setSelectedMedia(null)}>
          <div className="medialib-modal" onClick={(e) => e.stopPropagation()}>
            <button className="medialib-modal-close" onClick={() => setSelectedMedia(null)}>
              <i className="fas fa-times"></i>
            </button>
            <div className="medialib-modal-image">
              <img src={getImageUrl(selectedMedia)} alt={selectedMedia.originalname} />
            </div>
            <div className="medialib-modal-info">
              <h3>{selectedMedia.originalname}</h3>
              <div className="medialib-modal-details">
                <span><i className="fas fa-folder"></i> {selectedMedia.folder}</span>
                <span><i className="fas fa-weight-hanging"></i> {formatFileSize(selectedMedia.size)}</span>
                {selectedMedia.description && <span><i className="fas fa-align-left"></i> {selectedMedia.description}</span>}
              </div>
              {selectedMedia.tags?.length > 0 && (
                <div className="medialib-modal-tags">
                  {selectedMedia.tags.map(tag => <span key={tag} className="medialib-card-tag">{tag}</span>)}
                </div>
              )}
              <div className="medialib-modal-actions">
                <button className="medialib-submit-btn" onClick={() => copyToClipboard(getImageUrl(selectedMedia))}>
                  <i className="fas fa-copy"></i> Copiar URL
                </button>
                <button className="medialib-cancel-btn" onClick={() => setSelectedMedia(null)}>Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedMediaLibrary;
